<!-- 스케줄 작업 상세 -->
<template>
    <div class="detail-container">
        <!-- 1. 데이터 없음 표시 영역 -->
        <div class="detail-placeholder" v-if="!detailInfo">
            <span class="detail-notification-icon">
                <i class="fas fa-external-link-alt"></i>
            </span>
            <span class="detail-notification-text">{{ $t("작업을 선택하세요.") }}</span>
        </div>
        <!-- 2. 상세 정보 영역 -->
        <div style="height: calc(100% - 70px); overflow: auto" v-if="detailInfo">
            <!-- 2.1 상세 헤더 (제목 + 버튼) -->
            <div class="detail-header" v-if="detailInfo">
                <div class="detail-title">
                    <span class="title">{{ viewTitle }}</span>
                    <span class="badge title1">{{ detailInfo.name }}</span>
                    <span class="badge title2">{{ detailInfo.group }}</span>
                    <span class="badge others">{{ isPluginJob ? $t("플러그인") : $t("시스템") }}</span>
                </div>
                <div class="detail-header-buttons">
                    <button type="button" class="btn btn-sm btn-success" @click="handleRunNow">
                        {{ $t("지금 실행") }}
                    </button>
                    <template v-if="detailInfo">
                        <button v-if="isJobEnabled" type="button" class="btn btn-sm btn-warning" @click="handleDisable">
                            {{ $t("비활성화") }}
                        </button>
                        <button v-else type="button" class="btn btn-sm btn-info" @click="handleEnable">
                            {{ $t("활성화") }}
                        </button>
                    </template>
                    <!-- <button
                        type="button"
                        class="btn btn-sm btn-primary"
                        v-if="detailInfo && !isPluginJob"
                        @click="handleSave"
                    >
                        {{ $t("저장") }}
                    </button> -->
                    <button
                        type="button"
                        class="btn btn-sm btn-secondary"
                        v-if="detailInfo"
                        @click="handleClose($event)"
                    >
                        {{ $t("닫기") }}
                    </button>
                </div>
            </div>

            <!-- 2.2 기본 정보 + 트리거 + 다음 실행 시간 -->
            <div class="detail-content" v-if="detailInfo">
                <div class="detail-section">
                    <div class="detail-section-title">{{ $t("기본 정보") }}</div>
                    <table class="detail-table">
                        <tbody>
                            <tr>
                                <th>{{ $t("작업명") }}</th>
                                <td>{{ detailInfo.name }}</td>
                            </tr>
                            <tr>
                                <th>{{ $t("그룹") }}</th>
                                <td>{{ detailInfo.group }}</td>
                            </tr>
                            <tr>
                                <th>{{ $t("설명") }}</th>
                                <td>{{ detailInfo.remark || "-" }}</td>
                            </tr>
                            <tr v-if="detailInfo.updDt">
                                <th>{{ $t("최종수정일") }}</th>
                                <td>{{ formatDateStr(detailInfo.updDt) }}</td>
                            </tr>
                            <tr>
                                <th>{{ $t("마지막 실행") }}</th>
                                <td>{{ formatDateStr(detailInfo.lastRun) || "-" }}</td>
                            </tr>
                            <tr v-if="nextExecutionTime !== null">
                                <th>{{ $t("다음 실행 시간") }}</th>
                                <td>{{ nextExecutionTime }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="detail-section">
                    <div class="detail-section-title">{{ $t("트리거") }}</div>
                    <table class="detail-table triggers-table">
                        <thead>
                            <tr>
                                <th>{{ $t("타입") }}</th>
                                <th>{{ $t("스케줄") }}</th>
                                <th>{{ $t("시작") }}</th>
                                <th>{{ $t("종료") }}</th>
                                <th>{{ $t("사용") }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(t, i) in detailInfo.triggers || []" :key="i">
                                <td>{{ t.scheduleType || "-" }}</td>
                                <td>
                                    {{
                                        t.cronExpression || (t.interval ? `${t.interval} ${t.intervalType || ""}` : "-")
                                    }}
                                </td>
                                <td>{{ formatDateStr(t.startDt) || t.startDt || "-" }}</td>
                                <td>{{ formatDateStr(t.endDt) || t.endDt || "-" }}</td>
                                <td>{{ t.enable ? $t("사용중") : $t("미사용") }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import backEndApi from "@src/api/backEndApi";
import * as popupMessages from "@src/consts/popupMessageConsts";

export default {
    components: {},
    props: {
        title: { type: String, default: () => "스케줄 작업" },
        scheduleName: { type: String, default: null },
    },
    data() {
        return {
            detailInfo: null,
            nextExecutionTime: null,
            mode: "view",
        };
    },
    computed: {
        viewTitle() {
            return this.title;
        },
        isPluginJob() {
            return !!(this.detailInfo && (this.detailInfo.pluginName || this.detailInfo.pluginJobName));
        },
        isJobEnabled() {
            if (!this.detailInfo || !this.detailInfo.triggers || !this.detailInfo.triggers.length) return false;
            return this.detailInfo.triggers.some((t) => t.enable);
        },
    },
    watch: {
        scheduleName() {
            if (!this.scheduleName) {
                this.clearData();
            } else {
                this.loadData();
            }
        },
    },
    methods: {
        formatDateStr(val) {
            if (!val) return "";
            const s = String(val).trim();
            if (s.length === 14) {
                const y = s.slice(0, 4),
                    m = s.slice(4, 6),
                    d = s.slice(6, 8);
                const h = s.slice(8, 10),
                    min = s.slice(10, 12),
                    sec = s.slice(12, 14);
                return `${y}-${m}-${d} ${h}:${min}:${sec}`;
            }
            if (s.length === 8) {
                return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
            }
            return val;
        },

        async notifyCancelEdit() {
            if (this.mode === "edit") {
                const result = await this.alertConfirmWarning("편집중인 내용을 무시하고 종료할까요?");
                return !!result.value;
            }
            return true;
        },

        async loadData() {
            if (!this.scheduleName) return;
            try {
                const result = await backEndApi.scheduleJob.getJob(this.scheduleName);
                if (result.status === 200 && result.data) {
                    this.detailInfo = { ...result.data };
                    this.mode = "view";
                    await this.loadNextExecutionTime();
                } else {
                    this.alertWarning(
                        result.data?.message || "조회 실패",
                        "",
                        typeof result.data?.detail === "string" ? result.data.detail : ""
                    );
                }
            } catch (e) {
                this.alertNoti(popupMessages.COMMON_NO_SEARCH_DATA_POPUP_MESSAGE || "데이터를 불러올 수 없습니다.");
                console.error(e);
            }
        },

        async loadNextExecutionTime() {
            if (!this.scheduleName) return;
            try {
                const result = await backEndApi.scheduleJob.getNextExecutionTime(this.scheduleName);
                if (result.status === 200 && result.data != null) {
                    const t = result.data.nextExecutionTime;
                    this.nextExecutionTime = t ? new Date(t).toLocaleString() : "-";
                } else {
                    this.nextExecutionTime = this.isJobEnabled ? "-" : null;
                }
            } catch (_) {
                this.nextExecutionTime = this.isJobEnabled ? "-" : null;
            }
        },

        clearData() {
            this.detailInfo = null;
            this.nextExecutionTime = null;
            this.mode = "view";
            this.$forceUpdate();
        },

        async handleRunNow() {
            if (!this.scheduleName) return;
            const result = await this.alertQuestion(
                popupMessages.SYSTEM_EVENT_LOG_SCHEDULE_POPUP_MESSAGE || "지금 실행할까요?"
            );
            if (!result.value) return;
            try {
                const res = await backEndApi.scheduleJob.runJob(this.scheduleName);
                if (res.status === 200 && !this.$err(res?.data)) {
                    this.alertNoti(
                        popupMessages.SYSTEM_EVENT_LOG_SCHEDULE_SUCCESS_POPUP_MESSAGE || "실행 요청되었습니다."
                    );
                    await this.loadData();
                    this.$emit("data-updated");
                } else {
                    this.alertWarning(res?.data?.message || "실행 실패", "", res?.data?.detail);
                }
            } catch (e) {
                console.error(e);
                this.alertWarning("실행 중 오류가 발생했습니다.");
            }
        },

        async handleEnable() {
            if (!this.scheduleName) return;
            try {
                const res = await backEndApi.scheduleJob.enableJob(this.scheduleName);
                if (res.status === 200 && !this.$err(res?.data)) {
                    this.alertNoti("활성화되었습니다.");
                    await this.loadData();
                    this.$emit("data-updated");
                } else {
                    this.alertWarning(res?.data?.message || "활성화 실패", "", res?.data?.detail);
                }
            } catch (e) {
                console.error(e);
                this.alertWarning("활성화 중 오류가 발생했습니다.");
            }
        },

        async handleDisable() {
            if (!this.scheduleName) return;
            const result = await this.alertConfirmWarning("비활성화하면 스케줄 실행이 중지됩니다. 계속할까요?");
            if (!result.value) return;
            try {
                const res = await backEndApi.scheduleJob.disableJob(this.scheduleName);
                if (res.status === 200 && !this.$err(res?.data)) {
                    this.alertNoti("비활성화되었습니다.");
                    await this.loadData();
                    this.$emit("data-updated");
                } else {
                    this.alertWarning(res?.data?.message || "비활성화 실패", "", res?.data?.detail);
                }
            } catch (e) {
                console.error(e);
                this.alertWarning("비활성화 중 오류가 발생했습니다.");
            }
        },

        async handleSave() {
            if (!this.detailInfo || this.isPluginJob) return;
            try {
                const res = await backEndApi.scheduleJob.saveJob(this.detailInfo);
                if (res.status === 200 && !this.$err(res?.data)) {
                    this.alertNoti(popupMessages.COMMON_SAVE_POPUP_MESSAGE || "저장되었습니다.");
                    this.$emit("data-updated");
                    await this.loadData();
                } else {
                    this.alertWarning(res?.data?.message || "저장 실패", "", res?.data?.detail);
                }
            } catch (e) {
                console.error(e);
                this.alertWarning("저장 중 오류가 발생했습니다.");
            }
        },

        async handleClose(event) {
            const ok = await this.notifyCancelEdit();
            if (!ok) {
                if (event) event.preventDefault();
                return false;
            }
            this.clearData();
            this.$emit("closed");
            return true;
        },
    },
};
</script>

<style scoped>
.detail-container {
    height: 100%;
    width: 100%;
    padding: 1rem;
    border-radius: 10px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    font-size: 12px;
}

.detail-placeholder {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.detail-notification-icon {
    padding: 1rem;
    color: #444;
    font-size: 48px;
}

.detail-notification-text {
    font-size: 15px;
    font-weight: bold;
}

.detail-header {
    margin-bottom: 0;
    padding: 8px 16px;
    overflow: hidden;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-radius: 6px 6px 0 0;
    background-color: #f1f1f1;
    border: 1px solid #ececec;
    border-bottom: 1px solid #ccc;
    font-weight: bold;
}

.detail-header .detail-title {
    padding: 0;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
}

.detail-header-buttons {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-left: auto;
}

.detail-content {
    margin-top: 2px;
    padding: 16px;
    overflow: auto;
    background-color: white;
    border: 1px solid #ececec;
    border-radius: 0 0 6px 6px;
}

.detail-section {
    margin-bottom: 16px;
}

.detail-section-title {
    font-weight: 600;
    margin-bottom: 8px;
}

.detail-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
}

.detail-table th,
.detail-table td {
    border: 1px solid #eee;
    padding: 6px 10px;
    text-align: left;
}

.detail-table th {
    width: 120px;
    background: #f9f9f9;
}

.triggers-table th {
    width: auto;
}

.detail-bottom {
    margin-top: auto;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-top: 12px;
}

.detail-bottom-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 4px;
}

.detail-btn {
    flex: 0 0 auto;
}

.detail-bottom-info {
    font-size: 11px;
    color: #666;
}

.title {
    font-weight: 600;
    margin-right: 12px;
}

.badge {
    font-size: 12px;
    font-weight: 500;
    margin-right: 4px;
    display: inline-block;
    min-width: 20px;
    padding: 0 10px;
    color: #fff;
    text-align: center;
    vertical-align: middle;
    white-space: nowrap;
    line-height: 22px;
    border-radius: 8px;
}

.badge.title1 {
    background-color: #ff6d00;
}

.badge.title2 {
    background-color: #546e7a;
}

.badge.others {
    background-color: #cfd8dc;
    color: #000;
}
</style>
